import { useEffect, useRef, useState } from "react";
import { UserRole } from "../../enums";
import { IStudioSelectItemModel } from "../models/studioSelectItemModel";
import { endpointsMetadata } from "../routes/endpointsMetadata";
import {
  isGatedStudioAccess,
  isStudioAccessConfirmed,
  isStudioAccessSilent,
  markStudioAccessConfirmed,
  markStudioAccessConfirmedSilently,
  requiresStudioSwitchConfirmation,
} from "../utilities/studioAccessUtility";
import { useApiMutation } from "./useApiMutation";

export interface IUseStudioAccessConfirmationArgs {
  currentUserRole?: UserRole;
  userStudios: IStudioSelectItemModel[];
  selectedStudioId: number;
  /** A `?selectedStudio=` link is still being handled; the startup check waits for it. */
  hasPendingUrlStudio: boolean;
  /** The studio list request failed; nothing is held back waiting for it. */
  hasStudiosLoadFailed: boolean;
  /** Makes the studio the selected one, with no questions asked. */
  applyStudioSelection: (studioId: number) => void;
  /** Drops the selection, which brings up the studio picker. */
  clearStudioSelection: () => void;
}

interface IPendingStudioAccess {
  studio: IStudioSelectItemModel;
  /** "switch" — the admin picked a studio; "startup" — the app opened in a remembered one. */
  origin: "switch" | "startup";
  onSwitched?: () => void;
  /** The real studio the admin is leaving with this switch, if any. */
  leavingStudioId?: number;
}

/**
 * Gates a global admin's access to other people's studios: before a studio opens, the
 * admin confirms it in a dialog, and the confirmed access is recorded on the server (the
 * owner sees it in "Моят профил" → "Достъп от Planny"). The studio only opens once the
 * record is saved. Owners, staff and training studios are never gated.
 *
 * Two entry points:
 *  - requestStudioSwitch: every explicit switch (sidebar picker, studio dialog, links). Once a
 *    switch away from a real studio has happened, leaving it is logged too (best-effort).
 *  - the startup check: the app opened straight into a remembered studio that was not
 *    confirmed in this browser session. Saying "no" there clears the selection.
 *
 * Until that startup check is answered, isStudioAccessCheckPending holds the remembered
 * studio back: the layout does not render the page, so none of the studio's data is
 * fetched or shown before the admin has confirmed.
 */
export const useStudioAccessConfirmation = ({
  currentUserRole,
  userStudios,
  selectedStudioId,
  hasPendingUrlStudio,
  hasStudiosLoadFailed,
  applyStudioSelection,
  clearStudioSelection,
}: IUseStudioAccessConfirmationArgs) => {
  const [pending, setPending] = useState<IPendingStudioAccess>();
  const isRecordingRef = useRef(false);
  // Studios already confirmed or asked about at startup in this page load, so a refetch
  // of the studio list (or the 12-hour marker running out mid-session) never re-asks.
  const startupHandledRef = useRef(new Set<number>());

  const { mutate: recordAccess, isLoading: isRecordingStudioAccess } = useApiMutation();
  const { mutate: recordExit } = useApiMutation();

  const selectedStudio = userStudios.find((x) => x.id === selectedStudioId);

  useEffect(() => {
    if (pending || hasPendingUrlStudio || !selectedStudioId) {
      return;
    }

    if (
      !isGatedStudioAccess(currentUserRole, selectedStudio) ||
      startupHandledRef.current.has(selectedStudioId)
    ) {
      return;
    }

    startupHandledRef.current.add(selectedStudioId);

    if (!isStudioAccessConfirmed(selectedStudioId)) {
      setPending({ studio: selectedStudio as IStudioSelectItemModel, origin: "startup" });
    }
  }, [currentUserRole, userStudios, selectedStudioId, hasPendingUrlStudio, pending]);

  const isStudioAccessCheckPending =
    pending?.origin === "startup" ||
    (currentUserRole === UserRole.GlobalAdmin &&
      selectedStudioId > 0 &&
      // Studios still loading: whether this one is a training studio is not known yet.
      // A failed load holds nothing back, or the app would stay blank with no way out.
      (selectedStudio
        ? !selectedStudio.isTraining
        : userStudios.length === 0 && !hasStudiosLoadFailed) &&
      !startupHandledRef.current.has(selectedStudioId) &&
      !isStudioAccessConfirmed(selectedStudioId));

  /**
   * Logs that the admin left a real studio. Best-effort and silent: the switch has already
   * happened, and a lost exit line must not get in the admin's way.
   */
  const logLeaving = (studioId: number | undefined) => {
    if (!studioId) {
      return;
    }

    recordExit({
      endpoint: endpointsMetadata.studioAuditLog.recordGlobalAdminExit,
      method: "post",
      payload: { studioId },
      disableSuccessToast: true,
      disableErrorToast: true,
    });
  };

  const requestStudioSwitch = (studioId: number, onSwitched?: () => void) => {
    // The remembered studio's own check comes first; replacing it would open that
    // studio behind the new dialog without a record.
    if (pending?.origin === "startup") {
      return;
    }

    const studio = userStudios.find((x) => x.id === studioId);
    // A studio opened through the long-press back door left no opening entry, so it leaves
    // no exit entry either.
    const leavingStudioId =
      studioId !== selectedStudioId &&
      isGatedStudioAccess(currentUserRole, selectedStudio) &&
      !isStudioAccessSilent(selectedStudioId)
        ? selectedStudioId
        : undefined;

    if (!studio || !requiresStudioSwitchConfirmation(currentUserRole, studio, selectedStudioId)) {
      applyStudioSelection(studioId);
      onSwitched?.();
      logLeaving(leavingStudioId);
      return;
    }

    setPending({ studio, origin: "switch", onSwitched, leavingStudioId });
  };

  /**
   * `reason` — why the admin is opening the studio. Required: the dialog and the API both
   * reject a blank one.
   */
  const confirmStudioAccess = (reason?: string) => {
    if (!pending || isRecordingRef.current) {
      return;
    }

    const { studio, origin, onSwitched, leavingStudioId } = pending;
    const trimmedReason = reason?.trim();
    isRecordingRef.current = true;

    recordAccess(
      {
        endpoint: endpointsMetadata.studioAuditLog.recordGlobalAdminAccess,
        method: "post",
        payload: trimmedReason
          ? { studioId: studio.id, reason: trimmedReason }
          : { studioId: studio.id },
        disableSuccessToast: true,
      },
      {
        onSuccess: () => {
          isRecordingRef.current = false;
          markStudioAccessConfirmed(studio.id);
          startupHandledRef.current.add(studio.id);
          setPending(undefined);

          if (origin === "switch") {
            applyStudioSelection(studio.id);
            onSwitched?.();
            logLeaving(leavingStudioId);
          }
        },
        onError: () => {
          // The error toast comes from useApiMutation. The studio stays closed:
          // an opening the owner cannot see must not happen.
          isRecordingRef.current = false;
          setPending(undefined);

          if (origin === "startup") {
            clearStudioSelection();
          }
        },
      },
    );
  };

  /**
   * Opens the studio without recording anything — the deliberate back door behind a long press
   * on "Да, отвори". The studio is still marked confirmed for the session so the dialog does not
   * reappear, but no access or exit entry is written: not for leaving the previous studio, and
   * (through the silent marker) not for leaving this one later.
   */
  const confirmStudioAccessSilently = () => {
    if (!pending || isRecordingRef.current) {
      return;
    }

    const { studio, origin, onSwitched } = pending;
    markStudioAccessConfirmedSilently(studio.id);
    startupHandledRef.current.add(studio.id);
    setPending(undefined);

    if (origin === "switch") {
      applyStudioSelection(studio.id);
      onSwitched?.();
    }
  };

  const cancelStudioAccess = () => {
    if (!pending || isRecordingRef.current) {
      return;
    }

    const { origin } = pending;
    setPending(undefined);

    if (origin === "startup") {
      clearStudioSelection();
    }
  };

  return {
    requestStudioSwitch,
    pendingStudioAccess: pending?.studio,
    confirmStudioAccess,
    confirmStudioAccessSilently,
    cancelStudioAccess,
    isRecordingStudioAccess,
    isStudioAccessCheckPending,
  };
};
